import { ContactPoint, Patient, RelatedPerson } from '@medplum/fhirtypes';
import { HL7, IDENTIFIERS } from '@consts/systems.consts';
import { ChoremeioFieldConfigItem } from '../configuration/components/choremeioFields.config';
import { t } from 'i18next';
import { GENDER_OPTIONS } from '@/consts/patient.creation.consts';

const normalizePhone = (phone: string | undefined) => {
  if (!phone) {
    return '';
  }
  return phone.startsWith('+') ? phone : `+${phone}`;
};

export interface PatientCreationFormFields {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  addressLine?: string;
  city?: string;
  postalCode?: string;
  amka?: string;
  birthdate?: string | Date;
  gender?: string;
}

export const getFormValuesFromPatient = (
  patient: Patient
): PatientCreationFormFields => {
  return {
    firstName: patient?.name?.[0]?.given?.[0] || '',
    lastName: patient?.name?.[0]?.family || '',
    email: patient?.telecom?.find((t) => t.system === 'email')?.value || '',
    phone: patient?.telecom?.find((t) => t.system === 'phone')?.value || '',
    addressLine: patient?.address?.[0]?.line?.[0] || '',
    city: patient?.address?.[0]?.city || '',
    postalCode: patient?.address?.[0]?.postalCode || '',
    birthdate: patient?.birthDate || '',
    gender: patient?.gender || '',
    amka:
      patient?.identifier?.find((id) => id.system === IDENTIFIERS.AMKA)
        ?.value || '',
  };
};

export const buildPatientSubmissionData = async (
  values: PatientCreationFormFields
): Promise<Partial<Patient>> => {
  const data: Partial<Patient> = {};

  if (values.firstName || values.lastName) {
    data.name = [
      {
        use: 'official',
        ...(values.lastName && { family: values.lastName }),
        ...(values.firstName && { given: [values.firstName] }),
      },
    ];
  }

  const telecom: ContactPoint[] = [];

  if (values.email) {
    telecom.push({
      system: 'email',
      value: values.email,
      use: 'home',
    });
  }

  if (values.phone) {
    telecom.push({
      system: 'phone',
      value: normalizePhone(values.phone),
      use: 'mobile',
    });
  }

  if (telecom.length > 0) {
    data.telecom = telecom;
  }

  if (values.addressLine || values.city || values.postalCode) {
    data.address = [
      {
        use: 'home',
        ...(values.addressLine && { line: [values.addressLine] }),
        ...(values.city && { city: values.city }),
        ...(values.postalCode && { postalCode: values.postalCode }),
      },
    ];
  }

  if (values.amka) {
    data.identifier = [
      {
        system: IDENTIFIERS.AMKA,
        value: values.amka,
      },
    ];
  }
  if (values.birthdate) {
    const date =
      values.birthdate instanceof Date
        ? values.birthdate
        : new Date(values.birthdate);
    data.birthDate = date.toISOString().split('T')[0];
  }

  if (values.gender) {
    data.gender =
      values.gender as (typeof GENDER_OPTIONS)[keyof typeof GENDER_OPTIONS];
  }

  return data;
};

export type RelatedPersonType = 'father' | 'mother' | 'spouse';

export const getFormValuesFromRelatedPerson = (
  person: RelatedPerson | null,
  type: RelatedPersonType
): Record<string, string> => {
  return {
    [`${type}LastName`]: person?.name?.[0]?.family || '',
    [`${type}FirstName`]: person?.name?.[0]?.given?.[0] || '',
    [`${type}Amka`]:
      person?.identifier?.find((id) => id.system === IDENTIFIERS.AMKA)?.value ||
      '',
    [`${type}Origin`]: person?.address?.[0]?.city || '',
    [`${type}Email`]:
      person?.telecom?.find((t) => t.system === 'email')?.value || '',
    [`${type}Phone`]: normalizePhone(
      person?.telecom?.find((t) => t.system === 'phone')?.value
    ),
  };
};

export const buildRelatedPersonSubmissionData = (
  values: Record<string, any>,
  type: RelatedPersonType,
  patientResource: { id?: string }
): RelatedPerson | null => {
  const relationshipCodes: Record<RelatedPersonType, string> = {
    father: HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.FATHER,
    mother: HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.MOTHER,
    spouse: HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.SPOUSE,
  };

  const relevantFields = [
    `${type}LastName`,
    `${type}FirstName`,
    `${type}Amka`,
    `${type}Origin`,
    `${type}Email`,
    `${type}Phone`,
  ];

  const hasAnyInfo = relevantFields.some(
    (key) => values[key] !== undefined && values[key]?.toString().trim() !== ''
  );

  if (!hasAnyInfo) {
    return null;
  }

  const relatedPerson: RelatedPerson = {
    resourceType: 'RelatedPerson',
    patient: { reference: `Patient/${patientResource.id}` },
    relationship: [
      {
        coding: [
          {
            system: HL7.TERMINOLOGY.CS.V3.ROLE_CODE.BASE,
            code: relationshipCodes[type],
            display: type,
          },
        ],
      },
    ],

    ...((values[`${type}LastName`] || values[`${type}FirstName`]) && {
      name: [
        {
          use: 'official',
          ...(values[`${type}LastName`] && {
            family: values[`${type}LastName`],
          }),
          ...(values[`${type}FirstName`] && {
            given: [values[`${type}FirstName`]],
          }),
        },
      ],
    }),

    ...(values[`${type}Amka`] && {
      identifier: [
        {
          system: IDENTIFIERS.AMKA,
          value: values[`${type}Amka`],
        },
      ],
    }),
    ...(values[`${type}Origin`] && {
      address: [
        {
          use: 'home',
          city: values[`${type}Origin`],
        },
      ],
    }),
  };

  // Build telecom array to support both email and phone
  const telecom: ContactPoint[] = [];
  if (values[`${type}Email`]) {
    telecom.push({
      system: 'email',
      value: values[`${type}Email`],
      use: 'home',
    });
  }
  if (values[`${type}Phone`]) {
    telecom.push({
      system: 'phone',
      value: normalizePhone(values[`${type}Phone`]),
      use: 'mobile',
    });
  }
  if (telecom.length > 0) {
    relatedPerson.telecom = telecom;
  }

  return relatedPerson;
};

export const buildRelatedPersonPatientSubmissionData = (
  values: Record<string, any>,
  type: RelatedPersonType,
  relatedPersonResource?: { id?: string }
): Partial<Patient> => {
  const data: Partial<Patient> = {};
  const firstName = values[`${type}FirstName`];
  const lastName = values[`${type}LastName`];
  const amka = values[`${type}Amka`];
  const city = values[`${type}Origin`];
  const email = values[`${type}Email`];
  const phone = values[`${type}Phone`];

  if (firstName || lastName) {
    data.name = [
      {
        use: 'official',
        ...(lastName && { family: lastName }),
        ...(firstName && { given: [firstName] }),
      },
    ];
  }

  if (city) {
    data.address = [
      {
        use: 'home',
        city,
      },
    ];
  }

  if (amka) {
    data.identifier = [
      {
        system: IDENTIFIERS.AMKA,
        value: amka,
      },
    ];
  }

  const telecom: ContactPoint[] = [];
  if (email) {
    telecom.push({
      system: 'email',
      value: email,
      use: 'home',
    });
  }

  if (phone) {
    telecom.push({
      system: 'phone',
      value: normalizePhone(phone),
      use: 'mobile',
    });
  }

  if (telecom.length > 0) {
    data.telecom = telecom;
  }

  if (relatedPersonResource) {
    data.link = [
      {
        other: { reference: `RelatedPerson/${relatedPersonResource.id}` },
        type: 'seealso',
      },
    ];
  }

  return data;
};

export const getInitialValues = (
  fieldsConfig: ChoremeioFieldConfigItem[]
): Record<string, any> => {
  return fieldsConfig.reduce<Record<string, any>>((acc, field) => {
    if (field.type === 'group') {
      field.fields.forEach((subField) => {
        acc[subField.fieldName] = subField.defaultValue || '';
      });
    } else if (field.type === 'field') {
      acc[field.fieldName] = field.defaultValue || '';
    }
    return acc;
  }, {});
};

export const getAnswerOptions = (
  fieldName: string,
  answerOptions: { value: string }[]
): { value: string; label: string }[] | null => {
  const translationNamespace: Record<string, string> = {
    gender: 'genders',
  };

  const translationKey = translationNamespace[fieldName];

  return answerOptions.map((option) => ({
    value: option.value,
    label: translationKey
      ? t(`${translationKey}.${option.value}` as any, {
          ns: 'create-patient-modal',
        })
      : option.value,
  }));
};
